import React, { Component } from 'react';
import PropTypes from 'prop-types';

// Sentry.
import * as Sentry from '@sentry/browser';

// Components.
import Exception from 'app/client/modules/Pages/Exception';

const INITIAL_STATE = {
  info: null,
  eventId: null,
};

class ErrorBoundary extends Component {
  /**
   * The class constructor.
   *
   * @param {Object} props The props passed to this component.
   */
  constructor(props) {
    super(props);

    this.state = { ...INITIAL_STATE };
  }

  /**
   * This lifecycle method is called after the component updates.
   * It checks if the children props have changed and resets the error state if they have.
   *
   * @param {Object} prevProps The previous props before the update.
   * @param {Object} prevState The previous state before the update.
   *
   */
  componentDidUpdate(prevProps, prevState) {
    if (prevState.eventId) {
      this.setState({ ...INITIAL_STATE });
    }
  }

  /**
   * When an error is thrown this method will catch the error and info.
   *
   * @param {string} error The error that was thrown.
   * @param {Object} info More detailed information about the thrown error.
   */
  componentDidCatch(error, info) {
    Sentry.withScope((scope) => {
      // Get the customer.
      const { customer } = this.props;

      // Set extras for the scope.
      scope.setExtras(info);
      scope.setUser(customer ? { name: customer.name } : false);

      // Get the event ID.
      const eventId = Sentry.captureException(error);

      // Set the state based on the error.
      this.setState({ info, eventId });
    });
  }

  /**
   * Render the error when it occurs, otherwise the children are rendered.
   *
   */
  render() {
    const { info, eventId } = this.state;
    const { children } = this.props;

    return info ? <Exception eventId={eventId} /> : children;
  }
}

/**
 * @type {Object} Default properties for this component to render.
 */
ErrorBoundary.defaultProps = {
  customer: false,
};

ErrorBoundary.propTypes = {
  customer: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
  ]),
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)])
    .isRequired,
};

export default ErrorBoundary;
